import type { Connector } from "wagmi";
import type { EIP1193Provider } from "viem";

export const WALLET_CHAIN_SWITCH_TIMEOUT_MS = 30_000;
const WALLET_CHAIN_POLL_INTERVAL_MS = 500;
const WALLET_CHAIN_READ_TIMEOUT_MS = 2_000;
const WALLET_CHAIN_ERROR_GRACE_MS = 1_500;

type WaitForWalletChainOptions = {
  timeoutMs?: number;
  pollIntervalMs?: number;
  readTimeoutMs?: number;
};

type SwitchWalletNetworkOptions = WaitForWalletChainOptions & {
  connector: Connector;
  provider: EIP1193Provider;
  chainId: number;
  rpcUrls: readonly string[];
  errorGraceMs?: number;
};

export type SwitchWalletNetworkResult = "switched" | "timeout";

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function parseChainId(value: unknown): number | null {
  if (typeof value === "number" && Number.isSafeInteger(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;

  const normalized = value.trim().toLowerCase();
  const parsed = Number.parseInt(normalized, normalized.startsWith("0x") ? 16 : 10);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export async function readWalletChainId(
  provider: EIP1193Provider,
  timeoutMs = WALLET_CHAIN_READ_TIMEOUT_MS,
): Promise<number | null> {
  const value = await Promise.race([
    provider.request({ method: "eth_chainId" }).catch(() => null),
    delay(timeoutMs).then(() => null),
  ]);
  return parseChainId(value);
}

export async function waitForWalletChain(
  provider: EIP1193Provider,
  chainId: number,
  options: WaitForWalletChainOptions = {},
): Promise<boolean> {
  const timeoutMs = options.timeoutMs ?? WALLET_CHAIN_SWITCH_TIMEOUT_MS;
  const pollIntervalMs = options.pollIntervalMs ?? WALLET_CHAIN_POLL_INTERVAL_MS;
  const readTimeoutMs = options.readTimeoutMs ?? WALLET_CHAIN_READ_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() <= deadline) {
    if ((await readWalletChainId(provider, readTimeoutMs)) === chainId) return true;

    const remaining = deadline - Date.now();
    if (remaining <= 0) return false;
    await delay(Math.min(pollIntervalMs, remaining));
  }

  return false;
}

/**
 * Switches a wallet without trusting its `chainChanged` event to arrive.
 *
 * Mobile wallets sometimes complete the approval but leave the connector's
 * promise pending forever. Polling `eth_chainId` confirms the wallet's actual
 * state; emitting the verified change then resolves wagmi and its connector.
 */
export async function switchWalletNetwork({
  connector,
  provider,
  chainId,
  rpcUrls,
  timeoutMs = WALLET_CHAIN_SWITCH_TIMEOUT_MS,
  pollIntervalMs = WALLET_CHAIN_POLL_INTERVAL_MS,
  readTimeoutMs = WALLET_CHAIN_READ_TIMEOUT_MS,
  errorGraceMs = WALLET_CHAIN_ERROR_GRACE_MS,
}: SwitchWalletNetworkOptions): Promise<SwitchWalletNetworkResult> {
  if ((await readWalletChainId(provider, readTimeoutMs)) === chainId) {
    connector.emitter.emit("change", { chainId });
    return "switched";
  }

  if (!connector.switchChain) {
    const error = new Error(`${connector.name} does not support switching chains.`);
    error.name = "SwitchChainNotSupportedError";
    throw error;
  }

  const switchRequest = connector.switchChain({
    chainId,
    addEthereumChainParameter: { rpcUrls: [...rpcUrls] },
  });

  // A successful request is not proof of a successful switch. Only a rejected
  // request should win this race; otherwise the provider poll is authoritative.
  const requestFailure = switchRequest.then<never, { error: unknown }>(
    () => new Promise<never>(() => undefined),
    (error) => ({ error }),
  );
  const detectedChain = waitForWalletChain(provider, chainId, {
    timeoutMs,
    pollIntervalMs,
    readTimeoutMs,
  }).then((switched) => ({ switched }));

  const outcome = await Promise.race([requestFailure, detectedChain]);

  if ("error" in outcome) {
    // MetaMask Mobile can report a failed request after it has already changed
    // networks. Give the provider a short grace period before showing failure.
    const recovered = await waitForWalletChain(provider, chainId, {
      timeoutMs: errorGraceMs,
      pollIntervalMs,
      readTimeoutMs,
    });
    if (!recovered) throw outcome.error;
  } else if (!outcome.switched) {
    return "timeout";
  }

  connector.emitter.emit("change", { chainId });
  return "switched";
}
